// What each `llm_routes` purpose IS — the one description of the pin vocabulary
// the whole platform reads (#3632).
//
// WHY IT IS HERE. It derives from `JOB_CLASS_ROUTES` in this same package and
// needs no I/O. Its one remaining external consumer is the MCP recipe that
// teaches an agent how to pin a route (`packages/mcp`, whose only internal deps
// are this package and observability); `packages/mcp` had a private copy of
// this table with a TODO to import the shared one, and putting the map
// anywhere mcp cannot import would have made that TODO permanent. A second
// consumer, `@developerz/billing`'s own ranking half, was removed in plan 102
// slice 02.
//
// AGENT-FACING ENGLISH ONLY. `what`/`wants` are the sentence the MCP recipe puts
// in front of a MODEL, over `PURPOSE_GUIDE`. The dashboard's model-per-purpose
// picker and the family-token ranker it drove were deleted with the Keys page's
// two model panels (#5212, epic #5200): model selection lives on the Agents
// page now, and nothing there localizes this table's prose — the Agents page's
// own controls carry their own `t()` copy.

import { BYOKTier } from '../entities/byok';
import {
  JobClass,
  LLM_ROUTE_PURPOSES,
  type LlmRoutePurposeAny,
  routeForJobClass,
} from './job-class-routing';

/**
 * How much model a purpose wants — the word the MCP recipe's bullet names
 * beside the tier it routes to (`purposeBullets`, `packages/mcp/src/recipes/
 * tokens.ts`), so a caller reads "the `heavy` tier (strongest)" rather than
 * the bare internal tier constant.
 *
 * THREE values over four `BYOKTier`s: `smart` and `heavy` both want a
 * provider's strongest family, so a word that split them would offer a
 * distinction nothing downstream reads.
 */
export const PURPOSE_WEIGHTS = ['fast', 'balanced', 'strongest'] as const;
export type PurposeWeight = (typeof PURPOSE_WEIGHTS)[number];

/**
 * The routing tier behind each weight. Total over {@link BYOKTier} by type, so a
 * fifth tier does not compile until someone decides what it means to a human
 * picking a model.
 */
const WEIGHT_BY_TIER: Readonly<Record<BYOKTier, PurposeWeight>> = Object.freeze({
  [BYOKTier.Fast]: 'fast',
  [BYOKTier.General]: 'balanced',
  [BYOKTier.Smart]: 'strongest',
  [BYOKTier.Heavy]: 'strongest',
});

/**
 * The tier for the purposes that are NOT job classes, so have no row in
 * `JOB_CLASS_ROUTES` to derive one from. Typed as the exact complement, so the
 * day a purpose joins {@link LLM_ROUTE_PURPOSES} that is not a {@link JobClass},
 * this object fails to compile rather than falling through to a default.
 *
 * - `review` — the reviewer reads a diff and argues about it. `smart`, the tier
 *   {@link routePurposeForEnvelope} already sends the review lane to.
 * - `gardener` — the KB janitor's nightly consolidation. Bulk, unattended,
 *   nobody waiting: `general`.
 * - `default` — the catch-all pin, used when a task's own purpose has none. The
 *   everyday tier, because it stands in for every purpose at once.
 * - `triage` — the judge that decides each ask (#6388): one structured verdict per
 *   ask, read against the board. A wrong verdict files the wrong work, so `smart`.
 * - `screen` — the intake brief screen (#6938): a quick yes/no classification of each
 *   brief, so `fast` — the tier an unpinned screen resolves (the worker's
 *   `purpose: Screen, tier: Fast`). Its own pin keeps it off the work model.
 */
const NON_JOB_CLASS_TIERS: Readonly<Record<Exclude<LlmRoutePurposeAny, JobClass>, BYOKTier>> =
  Object.freeze({
    review: BYOKTier.Smart,
    gardener: BYOKTier.General,
    default: BYOKTier.General,
    escalation: BYOKTier.Heavy,
    triage: BYOKTier.Smart,
    screen: BYOKTier.Fast,
  });

const JOB_CLASSES = new Set<string>(Object.values(JobClass));

/** Is this purpose one of the seven {@link JobClass} tags? */
function isJobClass(purpose: LlmRoutePurposeAny): purpose is JobClass {
  return JOB_CLASSES.has(purpose);
}

/**
 * The routing tier a purpose wants. DERIVED from `JOB_CLASS_ROUTES` for the seven
 * job classes — the table the dispatcher actually routes by, so this answer moves
 * when that one does — and stated only for the ones it has no row for.
 */
export function tierForPurpose(purpose: LlmRoutePurposeAny): BYOKTier {
  return isJobClass(purpose) ? routeForJobClass(purpose).tier : NON_JOB_CLASS_TIERS[purpose];
}

/**
 * The agent-facing English for each purpose — `what` it does, and `wants`, the
 * kind of model it should be pinned to.
 *
 * Exhaustive over {@link LlmRoutePurposeAny} by type, so a purpose added to
 * {@link LLM_ROUTE_PURPOSES} does not compile until somebody says what it is for.
 * That is the whole reason it is a table: the hand-written five-bullet paragraph
 * it replaced (in `packages/mcp`) named SCOUT/PLAN, CODING, REVIEW, GARDENER and
 * DEFAULT and silently omitted four purposes a caller can really pin
 * (`debugging-qa`, `general`, `quick`, `ci-triage`) — a recipe that taught a model
 * to pin six of ten.
 */
const PURPOSE_PROSE = {
  coding: {
    what: 'writes the change — new features, hard slices',
    wants: 'a strong coder with a large context window',
  },
  'debugging-qa': {
    what: 'reproduces and fixes a bug; long-context QA over a failing tree',
    wants: 'a model that holds a long transcript without losing the thread',
  },
  general: {
    what: 'ordinary edits and review triage — the everyday class',
    wants: 'a solid mid model; this one runs most often',
  },
  quick: {
    what: 'mechanical work — renames, doc touch-ups',
    wants: 'the cheapest fast model; reasoning buys nothing here',
  },
  scout: {
    what: 'reads the repo and reports what work exists',
    wants: 'a strong reader — it decides what the plan is made of',
  },
  plan: {
    what: 'authors an epic plan — the heaviest reasoning job',
    wants: 'your best model; a weak plan wastes every task under it',
  },
  'ci-triage': {
    what: 'reads a red CI run and says why — deterministic-first',
    wants: 'a fast cheap model; it fires often, reasons little',
  },
  review: {
    what: 'judges a finished diff (the AI reviewer and its thread replies)',
    wants:
      "a DIFFERENT model from the coder's — a model reviewing its own output agrees with itself",
  },
  gardener: {
    what: 'maintains the repo knowledge base — a janitor, not a task',
    wants: 'cheap and fast; it runs nightly, unwatched',
  },
  default: {
    what: 'the catch-all for any purpose with no pin of its own',
    wants: 'set this even if nothing else — everything unpinned lands here',
  },
  escalation: {
    what: 'retries a coding attempt whose red gate stopped getting closer',
    wants: "a STRONGER model than coding's — the same key works if it serves one",
  },
  triage: {
    what: 'decides each ask, a blocker answer included: schedule, drop, link or ask back',
    wants: 'a strong reasoner; unpinned, it uses the general route',
  },
  screen: {
    what: 'a quick yes/no injection screen of each brief',
    wants: 'a fast model off the work model, so its park cannot flip verdicts',
  },
} as const satisfies Record<LlmRoutePurposeAny, { what: string; wants: string }>;

/** One purpose's guide row. */
export type PurposeGuide = {
  /** Agent-facing English: what this purpose does. */
  readonly what: string;
  /** Agent-facing English: the kind of model to pin to it. */
  readonly wants: string;
  /** How much model this purpose wants — see {@link PurposeWeight}. */
  readonly weight: PurposeWeight;
  /** The routing tier the weight was derived from, for a caller that wants it exact. */
  readonly tier: BYOKTier;
};

/**
 * Every purpose's guide row — TOTAL over {@link LLM_ROUTE_PURPOSES}, built from
 * the enumeration itself so a purpose added there arrives here with no edit and
 * cannot be silently missing. `purpose-guide.test.ts` asserts the totality and
 * floors the corpus, so an emptied enumeration cannot pass as a complete map.
 */
export const PURPOSE_GUIDE: Readonly<Record<LlmRoutePurposeAny, PurposeGuide>> = Object.freeze(
  Object.fromEntries(
    LLM_ROUTE_PURPOSES.map((purpose) => {
      const tier = tierForPurpose(purpose);
      return [
        purpose,
        {
          ...PURPOSE_PROSE[purpose],
          weight: WEIGHT_BY_TIER[tier],
          tier,
        },
      ];
    }),
  ) as Record<LlmRoutePurposeAny, PurposeGuide>,
);
